import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import AlternativeSignIn from "../components/AlternativeSignIn";
import { Eye, EyeOff, RefreshCw } from "lucide-react";
import { siteConfig } from "../lib/siteConfig";

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState("");
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [resendMessage, setResendMessage] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [resetStep, setResetStep] = useState("request");
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetConfirm, setResetConfirm] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetErr, setResetErr] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setResendMessage(""); setNeedsVerification(false); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      nav("/dashboard");
    } catch (e) {
      const message = formatApiError(e);
      setErr(message);
      setNeedsVerification(e?.response?.status === 403 && /verify.*email/i.test(message));
    } finally { setLoading(false); }
  };

  const verifyCode = async () => {
    const cleanCode = verificationCode.replace(/\D/g, "");
    if (cleanCode.length !== 6) {
      setErr("Enter the 6-digit Gmail code.");
      return;
    }
    setErr("");
    setVerifyLoading(true);
    try {
      const { data } = await api.post("/auth/email/verify", { email, code: cleanCode });
      login(data.token, data.user);
      nav("/dashboard", { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setVerifyLoading(false);
    }
  };

  const resendVerification = async () => {
    if (!email) return;
    setErr("");
    setResendMessage("");
    setResendLoading(true);
    try {
      const { data } = await api.post("/auth/email/resend", { email });
      setResendMessage(data?.message || "Verification email sent. Check your Gmail.");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setResendLoading(false);
    }
  };

  const openForgotPassword = () => {
    setForgotOpen(true);
    setResetStep("request");
    setResetEmail(email);
    setResetCode("");
    setResetPassword("");
    setResetConfirm("");
    setResetErr("");
    setResetMessage("");
  };

  const requestResetCode = async () => {
    const targetEmail = resetEmail.trim();
    if (!targetEmail) {
      setResetErr("Enter your Gmail first.");
      return;
    }
    setResetErr("");
    setResetMessage("");
    setResetLoading(true);
    try {
      const { data } = await api.post("/auth/password/forgot", { email: targetEmail });
      setEmail(targetEmail);
      setResetStep("verify");
      setResetMessage(data?.message || "Gmail reset code sent.");
    } catch (e) {
      setResetErr(formatApiError(e));
    } finally {
      setResetLoading(false);
    }
  };

  const confirmPasswordReset = async () => {
    const cleanCode = resetCode.replace(/\D/g, "");
    if (cleanCode.length !== 6) {
      setResetErr("Enter the 6-digit Gmail code.");
      return;
    }
    if (resetPassword.length < 6) {
      setResetErr("New password must be at least 6 characters.");
      return;
    }
    if (resetPassword !== resetConfirm) {
      setResetErr("Passwords do not match.");
      return;
    }
    setResetErr("");
    setResetMessage("");
    setResetLoading(true);
    try {
      const { data } = await api.post("/auth/password/reset", {
        email: resetEmail.trim(),
        code: cleanCode,
        password: resetPassword,
      });
      setPassword("");
      setResetStep("done");
      setResetMessage(data?.message || "Password changed. Sign in with your new password.");
    } catch (e) {
      setResetErr(formatApiError(e));
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030705] text-white">
      <Navbar />
      <div className="max-w-md mx-auto px-6 pt-16 pb-24">
        <h1 className="font-display text-4xl font-black tracking-tighter mb-2">Welcome back</h1>
        <p className="text-white/60 mb-10">Sign in to your {siteConfig.name} account.</p>
        <AlternativeSignIn onSuccess={(data) => { login(data.token, data.user); nav("/dashboard", { replace: true }); }} />
        <form onSubmit={submit} autoComplete="on" className="space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">Email</label>
            <input data-testid="login-email" type="email" name="email" autoComplete="username" required value={email} onChange={(e)=>setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-[#0A130E] border border-white/10 focus:border-[#D4AF37] focus:outline-none" />
          </div>
          <div>
            <div className="flex items-center justify-between gap-3 mb-2">
              <label className="block text-xs uppercase tracking-[0.2em] text-white/50">Password</label>
              <button
                type="button"
                onClick={openForgotPassword}
                className="text-xs font-bold text-[#D4AF37] hover:text-[#F1C40F]"
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <input data-testid="login-password" type={showPassword ? "text" : "password"} name="password" autoComplete="current-password" required value={password} onChange={(e)=>setPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 rounded-lg bg-[#0A130E] border border-white/10 focus:border-[#D4AF37] focus:outline-none" />
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-white/50 hover:bg-white/10 hover:text-[#F1C40F]"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
          {err && <div data-testid="login-error" className="text-sm text-red-400 bg-red-900/20 border border-red-800/40 rounded-lg p-3">{err}</div>}
          {resendMessage && (
            <div className="text-sm text-emerald-300 bg-emerald-900/20 border border-emerald-700/40 rounded-lg p-3">
              {resendMessage}
            </div>
          )}
          {needsVerification && (
            <div className="space-y-3">
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="Gmail code"
                className="w-full px-4 py-3 rounded-lg bg-[#0A130E] border border-[#D4AF37]/40 focus:border-[#D4AF37] focus:outline-none text-center text-xl font-black tracking-[0.4em]"
              />
              <button
                type="button"
                onClick={verifyCode}
                disabled={verifyLoading || !email}
                className="w-full py-3 bg-[#D4AF37] text-black font-bold rounded-full hover:bg-[#F1C40F] transition-all disabled:opacity-50"
              >
                {verifyLoading ? "Verifying..." : "Verify code & sign in"}
              </button>
              <button
                type="button"
                onClick={resendVerification}
                disabled={resendLoading || !email}
                className="w-full py-3 border border-[#D4AF37]/40 text-[#F1C40F] font-bold rounded-full hover:bg-[#D4AF37]/10 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${resendLoading ? "animate-spin" : ""}`} />
                {resendLoading ? "Sending..." : "Resend Gmail code"}
              </button>
            </div>
          )}
          <button data-testid="login-submit" type="submit" disabled={loading}
            className="w-full py-3 bg-[#D4AF37] text-black font-bold rounded-full hover:-translate-y-0.5 hover:bg-[#F1C40F] transition-all disabled:opacity-50">
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>
        {forgotOpen && (
          <div className="mt-5 rounded-2xl border border-[#D4AF37]/30 bg-[#0A130E] p-4 space-y-4">
            <div>
              <p className="text-sm font-black text-white">Reset password</p>
              <p className="text-xs text-white/50 mt-1">Customer account recovery.</p>
            </div>
            {resetErr && <div className="text-sm text-red-400 bg-red-900/20 border border-red-800/40 rounded-lg p-3">{resetErr}</div>}
            {resetMessage && (
              <div className="text-sm text-emerald-300 bg-emerald-900/20 border border-emerald-700/40 rounded-lg p-3">
                {resetMessage}
              </div>
            )}
            {resetStep !== "done" && (
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">Email</label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  autoComplete="email"
                  className="w-full px-4 py-3 rounded-lg bg-[#030705] border border-white/10 focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
            )}
            {resetStep === "request" && (
              <button
                type="button"
                onClick={requestResetCode}
                disabled={resetLoading}
                className="w-full py-3 bg-[#D4AF37] text-black font-bold rounded-full hover:bg-[#F1C40F] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${resetLoading ? "animate-spin" : ""}`} />
                {resetLoading ? "Sending..." : "Send Gmail code"}
              </button>
            )}
            {resetStep === "verify" && (
              <div className="space-y-3">
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="Gmail code"
                  className="w-full px-4 py-3 rounded-lg bg-[#030705] border border-[#D4AF37]/40 focus:border-[#D4AF37] focus:outline-none text-center text-xl font-black tracking-[0.4em]"
                />
                <div className="relative">
                  <input
                    type={showResetPassword ? "text" : "password"}
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    autoComplete="new-password"
                    placeholder="New password"
                    className="w-full px-4 py-3 pr-12 rounded-lg bg-[#030705] border border-white/10 focus:border-[#D4AF37] focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetPassword((value) => !value)}
                    aria-label={showResetPassword ? "Hide new password" : "Show new password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-white/50 hover:bg-white/10 hover:text-[#F1C40F]"
                  >
                    {showResetPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showResetConfirm ? "text" : "password"}
                    value={resetConfirm}
                    onChange={(e) => setResetConfirm(e.target.value)}
                    autoComplete="new-password"
                    placeholder="Confirm new password"
                    className="w-full px-4 py-3 pr-12 rounded-lg bg-[#030705] border border-white/10 focus:border-[#D4AF37] focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetConfirm((value) => !value)}
                    aria-label={showResetConfirm ? "Hide confirm password" : "Show confirm password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-white/50 hover:bg-white/10 hover:text-[#F1C40F]"
                  >
                    {showResetConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={confirmPasswordReset}
                  disabled={resetLoading}
                  className="w-full py-3 bg-[#D4AF37] text-black font-bold rounded-full hover:bg-[#F1C40F] transition-all disabled:opacity-50"
                >
                  {resetLoading ? "Saving..." : "Set new password"}
                </button>
                <button
                  type="button"
                  onClick={requestResetCode}
                  disabled={resetLoading}
                  className="w-full py-3 border border-[#D4AF37]/40 text-[#F1C40F] font-bold rounded-full hover:bg-[#D4AF37]/10 transition-all disabled:opacity-50"
                >
                  Resend code
                </button>
              </div>
            )}
            {resetStep === "done" && (
              <button
                type="button"
                onClick={() => setForgotOpen(false)}
                className="w-full py-3 bg-[#D4AF37] text-black font-bold rounded-full hover:bg-[#F1C40F] transition-all"
              >
                Back to sign in
              </button>
            )}
          </div>
        )}
        <p className="mt-8 text-sm text-white/60 text-center">
          New here? <Link to="/signup" data-testid="login-signup-link" className="text-[#D4AF37] hover:underline">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
